<template>
  <el-card
    shadow="never"
    class="analysis-detail-card"
  >
    <div
      slot="header"
      class="analysis-detail-card__header"
    >
      {{ $t('analysisDetail.title') }}
    </div>
    <el-descriptions :column="3">
      <el-descriptions-item v-if="analysis.id">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-key" />
            {{ $t('analysisDetail.analysisNumber') }}
          </span>
        </template>
        <el-tag class="analysis-detail-card__value">
          {{ analysis.id }}
        </el-tag>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.code">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-box" />
            {{ $t('analysisDetail.code') }}
          </span>
        </template>
        <el-tag class="analysis-detail-card__value">
          {{ analysis.code }}
        </el-tag>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.description">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-document" />
            {{ $t('analysisDetail.description') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.description }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.methodCode">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-set-up" />
            {{ $t('analysisDetail.method') }}
          </span>
        </template>
        <el-tag class="analysis-detail-card__value">
          {{ analysis.methodCode }}
        </el-tag>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.method">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-tickets" />
            {{ $t('analysisDetail.methodDescription') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.method }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.brand">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-cpu" />
            {{ $t('analysisDetail.instrument') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.brand }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.family">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-collection" />
            {{ $t('analysisDetail.family') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.family }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item v-if="analysis.measure">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-odometer" />
            {{ $t('analysisDetail.measure') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.measure }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item v-if="laboratory">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-office-building" />
            {{ $t('analysisDetail.laboratory') }}
          </span>
        </template>
        <el-tag class="analysis-detail-card__value">
          {{ laboratory }}
        </el-tag>
      </el-descriptions-item>

      <el-descriptions-item v-if="classType">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-menu" />
            {{ $t('analysisDetail.classType') }}
          </span>
        </template>
        <el-tag class="analysis-detail-card__value">
          {{ classType }}
        </el-tag>
      </el-descriptions-item>

      <el-descriptions-item v-if="expirationDate">
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-date" />
            {{ $t('analysisDetail.expirationDate') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ expirationDate }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item>
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-medal" />
            {{ $t('analysisDetail.accreditedTest') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.accreditedTest ? '✓' : '—' }}
        </span>
      </el-descriptions-item>

      <el-descriptions-item>
        <template slot="label">
          <span class="analysis-detail-card__label">
            <i class="el-icon-finished" />
            {{ $t('analysisDetail.aifaTest') }}
          </span>
        </template>
        <span class="analysis-detail-card__text">
          {{ analysis.aifaTest ? '✓' : '—' }}
        </span>
      </el-descriptions-item>
    </el-descriptions>
  </el-card>
</template>

<script>
  import moment from 'moment';
  import { getAnalysisClassType } from '@/config/constants';

  export default {
    name: 'AnalysisDetailCard',
    props: {
      analysis: {
        type: Object,
        required: true,
      },
    },
    computed: {
      laboratory() {
        const { commodityCode } = this.analysis;
        const key = `constants.${commodityCode}`;
        return commodityCode && this.$te(key) ? this.$t(key) : commodityCode;
      },
      classType() {
        const classType = getAnalysisClassType(this.analysis.classType);
        return classType && this.$t(`enums.analysisClassTypes.${classType}`);
      },
      expirationDate() {
        const { expirationDate } = this.analysis;
        return expirationDate && moment(expirationDate).format('DD/MM/YYYY');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .analysis-detail-card {
    margin-bottom: 1rem;
    background-color: $--color-black-lighter;
    border: 1px solid $--border-color-base;

    &__header {
      color: $--color-text-primary;
      font-weight: 600;
    }

    &__label {
      display: inline-flex;
      align-items: center;
      color: $--color-text-regular;

      i[class^='el-icon-'] {
        margin-right: 0.35rem;
      }
    }

    ::v-deep .el-card__header {
      border-bottom: 1px solid $--border-color-base;
    }

    ::v-deep .el-descriptions__body {
      background-color: transparent;
    }

    ::v-deep .el-descriptions-item__content {
      color: $--color-text-primary;
    }

    &__value {
      height: auto;
      padding: 4px 10px;
      line-height: 1.5;
      white-space: normal;
      font-size: 0.85rem;
      font-weight: 600;
      background-color: rgba($--color-white, 0.1);
      border-color: rgba($--color-white, 0.18);
      color: $--color-text-primary;
    }

    &__text {
      font-size: 0.85rem;
      line-height: 1.5;
      color: $--color-text-primary;
    }

    ::v-deep .el-descriptions-item__cell {
      padding-bottom: 0.75rem;
    }

    ::v-deep .el-descriptions-item__container {
      align-items: center;

      .el-descriptions-item__label,
      .el-descriptions-item__content {
        align-items: center;
      }
    }
  }
</style>
